// Register Page - Elegir plan y crear cuenta (pago desde día 1)
import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import './Auth.css';

const PLANS = {
    basic: { name: 'Basic', price: '4,99', period: '/mes', annual: '56,99€/año (5% dto.)', projects: 1, users: 1 },
    pro: { name: 'Pro', price: '14,99', period: '/mes', annual: '170,99€/año (5% dto.)', projects: 'Ilimitados', users: 'Hasta 10' }
};

const Register = () => {
    const [searchParams] = useSearchParams();
    const planFromUrl = searchParams.get('plan') === 'pro' ? 'pro' : 'basic';
    const [selectedPlan, setSelectedPlan] = useState(planFromUrl);
    const [billing, setBilling] = useState('monthly'); // 'monthly' | 'annual'
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const { signInWithGitHub } = useAuth();

    useEffect(() => {
        setSelectedPlan(planFromUrl);
    }, [planFromUrl]);

    const handleGitHubLogin = async () => {
        setLoading(true);
        setError('');
        try {
            sessionStorage.setItem('roadmap_pending_checkout', JSON.stringify({
                plan: selectedPlan,
                billing
            }));
            await signInWithGitHub();
        } catch (err) {
            sessionStorage.removeItem('roadmap_pending_checkout');
            setError(err.message || 'Error al conectar con GitHub');
            setLoading(false);
        }
    };

    return (
        <div className="auth-container">
            <div className="auth-form-section">
                <Link to="/" className="auth-logo">
                    <div className="auth-logo-icon">⚡</div>
                    <span className="auth-logo-text">Roadmap<span>Platform</span></span>
                </Link>

                <div className="auth-content">
                    <h1>Elige tu plan y crea tu cuenta</h1>
                    <p className="auth-subtitle">
                        Pago desde el primer día. Conecta con GitHub para continuar.
                    </p>

                    {/* Plan selector */}
                    <div className="auth-plan-selector">
                        <button
                            type="button"
                            className={`auth-plan-option ${selectedPlan === 'basic' ? 'active' : ''}`}
                            onClick={() => setSelectedPlan('basic')}
                        >
                            <span className="plan-name">Basic</span>
                            <span className="plan-price">
                                {billing === 'annual' ? '56,99€/año' : '4,99€/mes'}
                                {billing === 'annual' && <span className="plan-price-note">(5% dto.)</span>}
                            </span>
                            <span className="plan-desc">1 proyecto, 1 usuario</span>
                        </button>
                        <button
                            type="button"
                            className={`auth-plan-option featured ${selectedPlan === 'pro' ? 'active' : ''}`}
                            onClick={() => setSelectedPlan('pro')}
                        >
                            <span className="plan-badge">Pro</span>
                            <span className="plan-name">Pro</span>
                            <span className="plan-price">
                                {billing === 'annual' ? '170,99€/año' : '14,99€/mes'}
                                {billing === 'annual' && <span className="plan-price-note">(5% dto.)</span>}
                            </span>
                            <span className="plan-desc">Proyectos ilimitados, hasta 10 usuarios</span>
                        </button>
                    </div>
                    <div className="auth-billing-toggle">
                        <button
                            type="button"
                            className={billing === 'monthly' ? 'active' : ''}
                            onClick={() => setBilling('monthly')}
                        >
                            Mensual
                        </button>
                        <button
                            type="button"
                            className={billing === 'annual' ? 'active' : ''}
                            onClick={() => setBilling('annual')}
                        >
                            Anual (5% dto.)
                        </button>
                    </div>

                    {error && <div className="auth-error">{error}</div>}

                    <button 
                        onClick={handleGitHubLogin} 
                        className="auth-github-btn"
                        disabled={loading}
                    >
                        <svg viewBox="0 0 24 24" fill="currentColor" className="github-icon">
                            <path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z" />
                        </svg>
                        {loading ? 'Conectando...' : `Continuar con GitHub · ${selectedPlan === 'pro' ? 'Pro' : 'Basic'} ${billing === 'annual' ? '(anual)' : '(mes)'}`}
                    </button>

                    <div className="auth-info-box">
                        <div className="auth-info-icon">💳</div>
                        <div className="auth-info-text">
                            <strong>Después de registrarte</strong>
                            <p>Te llevaremos al pago seguro con tarjeta (Stripe). Sin compromiso de permanencia.</p>
                        </div>
                    </div>

                    <div className="auth-features">
                        <div className="auth-feature">
                            <span className="feature-icon">📋</span>
                            <span>Roadmaps y tareas</span>
                        </div>
                        <div className="auth-feature">
                            <span className="feature-icon">🎤</span>
                            <span>Dictado por voz</span>
                        </div>
                        <div className="auth-feature">
                            <span className="feature-icon">👥</span>
                            <span>Compartir proyecto (Pro)</span>
                        </div>
                    </div>

                    <p className="auth-terms">
                        Al continuar, aceptas los <Link to="/terms">términos de uso</Link> y la <Link to="/privacy-policy">política de privacidad</Link>.
                    </p>

                    <div className="auth-no-github">
                        <p>¿No tienes GitHub?</p>
                        <a href="https://github.com/signup" target="_blank" rel="noopener noreferrer" className="create-github-link">
                            Crear cuenta de GitHub gratis →
                        </a>
                    </div>
                </div>
            </div>

            <div className="auth-visual-section">
                <div className="auth-visual-image">
                    <img
                        src="https://images.unsplash.com/photo-1551434678-e076c223a692?w=800&h=1200&fit=crop"
                        alt="Team working"
                    />
                </div>
                <div className="auth-visual-overlay"></div>
                <div className="auth-visual-text">
                    <h2>Únete a la plataforma</h2>
                    <p>Miles de desarrolladores ya gestionan sus proyectos con nosotros.</p>
                </div>
            </div>
        </div>
    );
};

export default Register;
